import {
  isReference,
  isResource,
  createReference as medplumCreateReference,
  getReferenceString as medplumGetReferenceString,
  resolveId as medplumResolveId,
} from '@medplum/core'
import type { Patient, Reference, Resource } from '@medplum/fhirtypes'
import { get } from 'lodash-es'
import { getDisplayString } from '#src/display.ts'
import { ExtendedResource } from '#src/ExtendedResource.ts'
import { invariant } from '#src/invariant.ts'
import { parseReferenceString } from '#src/utils.ts'

export function createReference<T extends Resource>(
  resource: T
): Reference<T> & { reference: string; display: string } {
  const display = getDisplayString(resource)
  return {
    ...medplumCreateReference(resource),
    display,
  }
}

export function getReferenceString(reference: Reference | Resource): string {
  const result = medplumGetReferenceString(reference)
  invariant(result, 'Reference string is undefined')
  return result
}

export const maybeGetReferenceString = medplumGetReferenceString

export function referenceToId(reference: Reference): string {
  const id = resolveId(reference)
  invariant(id, 'Reference must have an id')
  return id
}

export function referenceStringToId(referenceString: string): string {
  const [, id] = referenceString.split('/')
  invariant(id, 'Reference string must have an id')
  return id
}

export function referenceFromReferenceString<T extends Resource = Resource>(
  referenceString: string
): Reference<T> {
  const reference = parseReferenceString<T>(referenceString)
  invariant(reference, 'Invalid reference string')
  return reference
}

export function createReferenceStringWithHistory(value: Resource): string {
  invariant(isResource(value), 'Value must be a resource')
  invariant(value.meta?.versionId, 'Resource must have a version ID')
  return createReferenceVersionUrl(createReference(value), value.meta.versionId)
}

export function createReferenceVersionUrl(
  reference: Reference,
  versionId: string
): string {
  // Based on: https://github.com/medplum/medplum/blob/25c5743f4ee639b89f2205c66583b1d78bb261f2/packages/react/src/ResourceBlame/ResourceBlame.utils.ts#L3
  return `${getReferenceString(reference)}/_history/${versionId}`
}

export function createReferenceWithHistory<T extends Resource>(
  value: T
): Reference<T> & { reference: string; display: string } {
  return {
    display: getDisplayString(value),
    reference: createReferenceStringWithHistory(value),
  }
}

export function isReferenceString(value: unknown): value is string {
  if (typeof value !== 'string') {
    return false
  }
  // FHIR reference strings must be in format "ResourceType/id"
  // e.g., "Patient/123" or "Practitioner/abc"
  return /^[A-Z][A-Za-z]+\/[A-Za-z0-9\-.]+$/.test(value)
}

export function getReferenceVersionId(
  reference: Reference
): string | undefined {
  const [, versionId] = reference.reference?.split('/_history/') ?? []
  return versionId
}

// True when a FHIR reference string is versioned — it carries a `/_history/<v>`
// segment. The versioned form pins a reference to an exact resource version (the
// frozen-snapshot pattern); the unversioned form tracks the latest version.
export function isVersionedReferenceString(value: string): boolean {
  return value.includes('/_history/')
}

// Parse a history reference string (`Type/id/_history/{versionId}`, as built
// by `createReferenceStringWithHistory`) into its parts. Throws if the string
// is not a well-formed history ref.
export function parseHistoryReferenceString(historyRef: string) {
  const [reference, versionId] = historyRef.split('/_history/')
  invariant(versionId, `Not a history reference string: ${historyRef}`)
  const [resourceType, id] = reference.split('/')
  invariant(resourceType && id, `Not a history reference string: ${historyRef}`)
  return { resourceType, id, versionId }
}

// Heuristic `Patient/id` reference a FHIR resource belongs to, read from
// whichever of the common patient-pointing fields it carries — `subject`,
// `for`, `patient`, or `beneficiary` — and, for Appointments, the patient
// participant actor. Returns undefined when none points to a Patient. Use when
// you have an arbitrary resource and need its owning patient without
// type-switching per resourceType.
export function getPatientReferenceFromResource(
  resource: Resource | undefined
): Reference<Patient> | undefined {
  if (!resource) {
    return undefined
  }
  if (resource.resourceType === 'Patient') {
    return resource.id ? { reference: `Patient/${resource.id}` } : undefined
  }
  const direct = ['subject', 'for', 'patient', 'beneficiary']
    .map((field) => get(resource, field))
    .find(
      (value): value is Reference<Patient> =>
        patientReferenceString(value) !== undefined
    )
  if (direct) {
    return direct
  }
  if (resource.resourceType === 'Appointment') {
    const actor = (resource.participant ?? [])
      .map((participant) => participant.actor)
      .find(
        (a): a is Reference<Patient> => patientReferenceString(a) !== undefined
      )
    if (actor) {
      return actor
    }
  }
  return undefined
}

function patientReferenceString(
  value: Reference | undefined
): string | undefined {
  if (value && typeof value === 'object' && 'reference' in value) {
    const ref = (value as { reference?: unknown }).reference
    return typeof ref === 'string' && ref.startsWith('Patient/')
      ? ref
      : undefined
  }
  return undefined
}

/**
 * Recursive function to search for all references to the source resource, and translate them to the target resource
 * @param obj - A FHIR resource or element
 * @param srcReference - The reference string referring to the source resource
 * @param targetReference - The reference string referring to the target resource
 * @returns true if at least one reference was replaced, false otherwise
 */
export function replaceReferences(
  obj: any,
  srcReference: string,
  targetReference: string
): boolean {
  if (typeof obj !== 'object' || obj === null) {
    return false
  }

  let didReplace = false

  for (const [key, value] of Object.entries(obj)) {
    if (typeof value === 'object' && value !== null) {
      didReplace =
        replaceReferences(value, srcReference, targetReference) || didReplace
    } else if (typeof value === 'string' && value === srcReference) {
      obj[key] = targetReference
      didReplace = true
    }
  }

  return didReplace
}

export function resolveId(
  r: Reference | Resource | ExtendedResource<any>
): string | undefined {
  if (isReference(r) || isResource(r)) {
    return medplumResolveId(r)
  }

  return r.id
}

export function resolveReference<T extends Resource>(
  r: Reference<T> | T | ExtendedResource<T> | undefined
): Reference<T> | undefined {
  if (!r) {
    return undefined
  }

  if (isReference(r)) {
    return r as Reference<T>
  }

  if (isResource(r)) {
    return createReference(r)
  }

  return r instanceof ExtendedResource ? r.reference : undefined
}
